<template>
  <div class="employee-fair-pay-card-view">
    <div v-if="loading" class="employee-fair-pay-card-view__loading" role="status">
      Loading employee…
    </div>
    <EmployeeFairPayCardHeader
      v-else
      :name="headerName"
      :title="primaryPositionName"
      :avatar="employee.avatar || ''"
      :status="employee.current_status || employee.status || 'active'"
      :employee-id="resolvedId"
      :employee-data="employee"
      :primary-position-name="primaryPositionName"
      :breadcrumbs="breadcrumbs"
      :selected-tab="selectedTab"
      @onChange="selectedTab = $event"
      @breadcrumb-click="onBreadcrumbClick"
    >
      <CurrentPayPlanCard
        v-if="isPayPlansTab && payPlanCard"
        v-bind="payPlanCard"
        :loading="payPlanLoading"
        @open-position="onOpenPosition"
        @open-pay-range="onOpenPayRange"
      />
      <p v-else-if="isPayPlansTab && !payPlanLoading" class="employee-fair-pay-card-view__empty">
        This employee does not have a current pay plan.
      </p>
    </EmployeeFairPayCardHeader>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, getCurrentInstance } from "vue";
import { useRoute } from "vue-router/composables";
import EmployeeFairPayCardHeader from "@components/EmployeeFairPayCardHeader/EmployeeFairPayCardHeader.vue";
import CurrentPayPlanCard from "@components/CurrentPayPlanCard/CurrentPayPlanCard.vue";
import { getEmployeeByIdV2, listEmployeePayPlans } from "@/api/employeeAPI";
import { mapPayPlanListRow } from "@components/PayPlansTable/mapPayPlanListRow";
import { enrichCurrentPayPlanCard } from "@components/CurrentPayPlanCard/enrichCurrentPayPlanCard";
import {
  hasCurrentPayPlanDetail,
  mapCurrentPayPlanCard,
} from "@components/CurrentPayPlanCard/mapCurrentPayPlanCard";
import { FAIR_PAY_TAB, fairPayListRoute } from "@components/FairPayMenu/fairPayMenuConstants";
import { EMPLOYEE_FAIR_PAY_TAB } from "@components/EmployeeFairPayMenu/employeeFairPayMenuConstants";

const props = defineProps({
  employeeId: { type: [String, Number], default: null },
});

const route = useRoute();
const { proxy } = getCurrentInstance() || {};

const resolvedId = computed(() => props.employeeId || route.params.employeeId || route.params.id);

const loading = ref(true);
const employee = ref({});
const payPlanCard = ref(null);
const payPlanLoading = ref(false);
const payPlanRow = ref(null);
const selectedTab = ref(EMPLOYEE_FAIR_PAY_TAB.PAY_PLANS);

const isPayPlansTab = computed(() => selectedTab.value === EMPLOYEE_FAIR_PAY_TAB.PAY_PLANS);

const headerName = computed(() => {
  const first = employee.value.first_name || "";
  const last = employee.value.last_name || "";
  return `${first} ${last}`.trim() || "Employee";
});

const primaryPositionName = computed(
  () => employee.value.primary_position?.name || employee.value.title || ""
);

const breadcrumbs = computed(() => [
  { text: "Pay Plans", href: "/fair-pay" },
  { text: "Employee Fair Pay", disabled: true },
]);

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function onBreadcrumbClick(breadcrumb) {
  if (breadcrumb?.disabled) return;
  proxy?.$router?.push(fairPayListRoute(FAIR_PAY_TAB.PAY_PLANS)).catch(() => {});
}

function onOpenPosition() {
  const positionId = payPlanRow.value?.primaryPosition?.id;
  if (!positionId || !proxy?.$router) return;
  proxy.$router
    .push({ name: "FairPayPosition", params: { positionId: String(positionId) } })
    .catch(() => {});
}

function onOpenPayRange(code) {
  const payRangeId = payPlanCard.value?.payRange?.id;
  if (payRangeId && proxy?.$router) {
    proxy.$router
      .push({ name: "FairPayPayRangeDetail", params: { payRangeId: String(payRangeId) } })
      .catch(() => {});
    return;
  }
  if (code && proxy?.$router) {
    proxy.$router.push(fairPayListRoute(FAIR_PAY_TAB.PAY_RANGES)).catch(() => {});
  }
}

function findEmployeeRow(results, employeeId) {
  return (Array.isArray(results) ? results : []).find(
    (item) => String(item.id) === String(employeeId)
  );
}

function positionIdFromEmployee(person) {
  return person?.primary_position?.position_id || person?.primary_position?.position?.id || null;
}

async function loadPayPlanCard(accountId, employeeId, person) {
  payPlanLoading.value = true;
  try {
    const names = [person?.last_name, person?.first_name].filter(Boolean);
    let apiRow = null;
    for (const name of names) {
      const response = await listEmployeePayPlans(accountId, {
        employee_name: name,
        pageSize: 50,
      });
      apiRow = findEmployeeRow(response?.data?.results, employeeId);
      if (apiRow) break;
    }
    if (!apiRow) {
      const response = await listEmployeePayPlans(accountId, { pageSize: 50 });
      apiRow = findEmployeeRow(response?.data?.results, employeeId);
    }
    const row = apiRow ? mapPayPlanListRow(apiRow) : null;
    const positionId = positionIdFromEmployee(person);
    if (row && positionId) {
      row.primaryPosition = { ...row.primaryPosition, id: positionId };
    }
    payPlanRow.value = row;
    if (!hasCurrentPayPlanDetail(row)) {
      payPlanCard.value = null;
      return;
    }
    payPlanCard.value = mapCurrentPayPlanCard({ row });
    payPlanCard.value = await enrichCurrentPayPlanCard(row);
  } catch {
    /* keep the list-mapped card when enrichment fails */
  } finally {
    payPlanLoading.value = false;
  }
}

onMounted(async () => {
  const accountId = getAccountId();
  const id = resolvedId.value;
  if (!accountId || !id) {
    loading.value = false;
    return;
  }
  try {
    const response = await getEmployeeByIdV2({ account_id: accountId, employeeId: id });
    employee.value = response?.data || {};
  } catch {
    employee.value = {};
    if (proxy?.$notifications?.error) {
      proxy.$notifications.error("Failed to load employee");
    }
  } finally {
    loading.value = false;
  }

  await loadPayPlanCard(accountId, id, employee.value);
});
</script>

<style scoped>
.employee-fair-pay-card-view {
  width: 100%;
}

.employee-fair-pay-card-view__loading,
.employee-fair-pay-card-view__empty {
  padding: 32px 24px;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: #667085;
}
</style>
